/**
 * SamMessage — render Sam's chat replies with clickable links and bullet lists,
 * without pulling in a Markdown package. Handles three patterns:
 *   • Bare URLs and site-relative paths (/services/..., /courses/..., /book-consult?...)
 *   • [text](url) Markdown links
 *   • Lines starting with "- " become a bullet list
 *
 * The render output is intentionally lightweight to stay snappy inside the
 * chat widget bubble.
 */
import React from "react";

const URL_RX = /(https?:\/\/[^\s<>\)]+|\/(?:services|courses|book-consult|portal|book|class-series|enrolment)[^\s<>\)]*)/g;
const MD_LINK_RX = /\[([^\]]+)\]\(([^)]+)\)/g;
const BOLD_RX = /\*\*([^*]+)\*\*/g;

function renderInline(text, baseKey) {
  // Replace markdown links first → tokens, then bare URLs, then bold.
  const parts = [];
  let lastIndex = 0;
  // Step 1: handle [text](url)
  let working = "";
  let cursor = 0;
  let m;
  const placeholders = []; // collected JSX nodes keyed by ID
  const PLACEHOLDER = (n) => `\u0001LINK${n}\u0001`;
  text.replace(MD_LINK_RX, (full, label, url, offset) => {
    working += text.slice(cursor, offset);
    placeholders.push(
      <a key={`${baseKey}-md-${offset}`} href={url} target={url.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="text-[#1b4a76] font-semibold underline underline-offset-2 hover:text-[#f38838]">{label}</a>
    );
    working += PLACEHOLDER(placeholders.length - 1);
    cursor = offset + full.length;
  });
  working += text.slice(cursor);

  // Step 2: handle bare URLs
  const stage2 = [];
  let lastEnd = 0;
  working.replace(URL_RX, (url, _x, offset) => {
    if (offset > lastEnd) stage2.push(working.slice(lastEnd, offset));
    // Tidy fallback labels — Sam's prompt now wraps every link in
    // `[label](url)` Markdown but if a bare URL ever slips through we render
    // a friendly phrase instead of the raw URL for tidiness.
    let label = url;
    if (/\/book-consult/.test(url)) label = "Book here";
    else if (/\/courses\/online/.test(url)) label = "View online course";
    else if (/\/courses\/in-person/.test(url)) label = "View class details";
    else if (/\/portal/.test(url)) label = "Open in your portal";
    else if (/\/services\//.test(url)) label = "See service details";
    else if (/\/enrolment/.test(url)) label = "View enrolment";
    else if (/\/book/.test(url)) label = "Book here";
    placeholders.push(
      <a key={`${baseKey}-url-${offset}`} href={url} target={url.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="text-[#1b4a76] font-semibold underline underline-offset-2 hover:text-[#f38838]">{label}</a>
    );
    stage2.push(PLACEHOLDER(placeholders.length - 1));
    lastEnd = offset + url.length;
  });
  if (lastEnd < working.length) stage2.push(working.slice(lastEnd));
  const flattened = stage2.join("");

  // Step 3: split on placeholders and emit text + JSX
  const final = [];
  const re = /\u0001LINK(\d+)\u0001/g;
  let last = 0;
  let mm;
  while ((mm = re.exec(flattened)) !== null) {
    if (mm.index > last) {
      final.push(processBold(flattened.slice(last, mm.index), `${baseKey}-t-${last}`));
    }
    final.push(placeholders[Number(mm[1])]);
    last = mm.index + mm[0].length;
  }
  if (last < flattened.length) {
    final.push(processBold(flattened.slice(last), `${baseKey}-t-${last}`));
  }
  return final;
}

function processBold(text, baseKey) {
  const parts = [];
  let cursor = 0;
  let m;
  let i = 0;
  BOLD_RX.lastIndex = 0;
  while ((m = BOLD_RX.exec(text)) !== null) {
    if (m.index > cursor) parts.push(text.slice(cursor, m.index));
    parts.push(<strong key={`${baseKey}-b-${i++}`}>{m[1]}</strong>);
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <React.Fragment key={baseKey}>{parts}</React.Fragment>;
}

export default function SamMessage({ text }) {
  if (!text) return null;
  // Split into blocks separated by blank lines; render each as a paragraph or bullet group.
  const blocks = text.split(/\n\s*\n/);
  const out = [];
  blocks.forEach((block, bi) => {
    const lines = block.split("\n");
    const isList = lines.every((l) => l.trim().startsWith("- ") || l.trim().startsWith("• "));
    if (isList) {
      out.push(
        <ul key={`b-${bi}`} className="list-disc ml-5 my-1 space-y-0.5">
          {lines.map((l, li) => {
            const stripped = l.trim().replace(/^[-•]\s*/, "");
            return <li key={`b-${bi}-${li}`}>{renderInline(stripped, `b-${bi}-${li}`)}</li>;
          })}
        </ul>
      );
    } else {
      out.push(
        <p key={`b-${bi}`} className={bi === 0 ? "" : "mt-2"}>
          {lines.map((l, li) => (
            <React.Fragment key={`b-${bi}-l-${li}`}>
              {li > 0 && <br />}
              {renderInline(l, `b-${bi}-l-${li}`)}
            </React.Fragment>
          ))}
        </p>
      );
    }
  });
  return <div className="space-y-0">{out}</div>;
}
